import { Global } from '@emotion/react'
import React, { type CSSProperties, useEffect, useState } from 'react'
import {
  AlignItems,
  Body2,
  Box,
  BorderStyle,
  ButtonColorVariant,
  ButtonSize,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Heading5,
  Icon,
  IconVariant,
  JustifyContent,
  Modal,
  ModalBody,
  ModalButton,
  Space,
  Spinner,
  useTheme,
  withCitrusTheme,
  ZIndex,
} from '@gousto-internal/citrus-react'

type Props = {
  discount: {
    title: string
    subtitle: string
    discount: string
    applyCta: string
    skipCta: string
    isExistingDiscount: boolean
    promoCode: string | null
  } | null
  hasApplyDiscountError?: boolean
  hasSkipOrderError?: boolean
  isOpen: boolean
  isApplyingDiscount: boolean
  isSkipping: boolean
  modalName: string
  onApply: () => void
  onClose: () => void
  onSkip: () => void
  onView?: () => void
}

type MobileModalProps = {
  style?: {
    content?: CSSProperties
    overlay?: CSSProperties
  }
}

const DiscountIconBox = withCitrusTheme(Box, (theme) => ({
  color: theme.color(Color.White),
}))

const SKIP_ORDER_DISCOUNT_MODAL_PORTAL_CLASS = 'skip-order-discount-modal-portal'

const SKIP_ORDER_ERROR_MESSAGE =
  "We couldn't skip this order: Something went wrong, please try again. If it keeps happening, contact customer support."
const APPLY_DISCOUNT_ERROR_MESSAGE =
  "We couldn't apply this discount: Something went wrong, please try again. If it keeps happening, contact customer support."

const SkipOrderErrorCard = ({ message }: { message: string }) => (
  <Box
    bg={Color.Warning_50}
    borderColor={Color.Warning_200}
    borderWidth={0.5}
    borderStyle={BorderStyle.Solid}
    borderRadius={1}
    paddingH={3}
    paddingV={3}
    data-testid="alert-card"
  >
    <Body2 color={Color.ColdGrey_800}>{message}</Body2>
  </Box>
)

const SkipOrderDiscountModalClose = ({ onClose }: Pick<Props, 'onClose'>) => (
  <button
    aria-label="Close modal"
    onClick={onClose}
    style={{
      alignItems: 'center',
      appearance: 'none',
      background: 'transparent',
      border: 0,
      cursor: 'pointer',
      display: 'flex',
      flexShrink: 0,
      height: '24px',
      justifyContent: 'center',
      padding: 0,
      width: '24px',
    }}
    type="button"
  >
    <Icon name="close" size={6} variant={IconVariant.Actionable} />
  </button>
)

const getModalAppElement = () => {
  if (typeof document === 'undefined') {
    return undefined
  }

  return document.getElementById('react-root') ?? undefined
}

const useMobileBottomSheetStyle = (): MobileModalProps => {
  const { theme } = useTheme()
  const breakpoint = theme.tokens.breakpoints[0]
  const mediaQuery = `(max-width: ${Number.parseInt(breakpoint, 10) - 1}px)`
  const getIsMobile = () =>
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? false
      : window.matchMedia(mediaQuery).matches
  const [isMobile, setIsMobile] = useState(getIsMobile)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined
    }

    const media = window.matchMedia(mediaQuery)
    const handleChange = () => setIsMobile(media.matches)

    handleChange()

    if (media.addEventListener) {
      media.addEventListener('change', handleChange)

      return () => media.removeEventListener('change', handleChange)
    }

    media.addListener(handleChange)

    return () => media.removeListener(handleChange)
  }, [mediaQuery])

  if (!isMobile) {
    return {}
  }

  const style: MobileModalProps['style'] = {
    overlay: {
      alignItems: 'flex-end',
      backgroundColor: 'rgba(52, 63, 75, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      zIndex: theme.zIndex(ZIndex.Overlay),
    },
    content: {
      border: 'none',
      borderRadius: 0,
      bottom: 0,
      inset: 'auto 0 0 0',
      left: 0,
      padding: 0,
      position: 'absolute',
      right: 0,
      top: 'auto',
      width: '100%',
    },
  }

  return {
    style: {
      ...style,
    },
  }
}

export const SkipOrderDiscountModal = ({
  discount,
  hasApplyDiscountError = false,
  hasSkipOrderError = false,
  isOpen,
  isApplyingDiscount,
  isSkipping,
  modalName,
  onApply,
  onClose,
  onSkip,
  onView,
}: Props) => {
  const { theme } = useTheme()
  const mobileBreakpoint = Number.parseInt(theme.tokens.breakpoints[0], 10) - 1
  const mobileModalProps = useMobileBottomSheetStyle()
  const appElement = getModalAppElement()
  const isRequestInProgress = isApplyingDiscount || isSkipping

  if (!discount) {
    return null
  }

  return (
    <>
      <Global
        styles={{
          [`@media (max-width: ${mobileBreakpoint}px)`]: {
            [`.${SKIP_ORDER_DISCOUNT_MODAL_PORTAL_CLASS} .ReactModal__Content > div`]: {
              width: '100%',
            },
          },
        }}
      />
      <Modal
        isOpen={isOpen}
        name={modalName}
        {...(appElement ? { appElement } : { ariaHideApp: false })}
        contentLabel="Skip order discount modal"
        onAfterOpen={onView}
        onRequestClose={onClose}
        portalClassName={SKIP_ORDER_DISCOUNT_MODAL_PORTAL_CLASS}
        preventScroll
        shouldCloseOnEsc={!isRequestInProgress}
        shouldCloseOnOverlayClick={!isRequestInProgress}
        {...mobileModalProps}
      >
        <Box
          alignItems={AlignItems.Center}
          display={Display.Flex}
          justifyContent={JustifyContent.SpaceBetween}
          paddingH={4}
          paddingV={4}
          style={{ gap: '16px' }}
        >
          <Heading5>{discount.title}</Heading5>
          {!isRequestInProgress ? <SkipOrderDiscountModalClose onClose={onClose} /> : null}
        </Box>
        <Box
          borderColor={Color.ColdGrey_200}
          borderTopWidth={0}
          borderRightWidth={0}
          borderBottomWidth={0.5}
          borderLeftWidth={0}
          borderStyle={BorderStyle.Solid}
        />
        <ModalBody paddingH={0} paddingV={0}>
          <Box paddingH={[4, 6]} paddingBottom={[4, 6]} style={{ paddingTop: '10px' }}>
            <Body2 color={Color.ColdGrey_900}>{discount.subtitle}</Body2>
            <Space size={4} direction="vertical" />
            <Box
              alignItems={AlignItems.Center}
              bg={Color.Success_50}
              display={Display.Flex}
              flexDirection={FlexDirection.Row}
              paddingH={4}
              paddingV={4}
              style={{ borderRadius: '4px' }}
            >
              <DiscountIconBox
                alignItems={AlignItems.Center}
                bg={Color.Success_600}
                borderRadius={1.5}
                display={Display.Flex}
                height="48px"
                justifyContent={JustifyContent.Center}
                width="48px"
              >
                <Icon name="offer_percentage" size={8} variant={IconVariant.Inherit} />
              </DiscountIconBox>
              <Space size={5} direction="horizontal" />
              <Body2 color={Color.ColdGrey_900} fontWeight={FontWeight.Bold}>
                {discount.discount}
              </Body2>
            </Box>
            <Space size={[3, 4]} direction="vertical" />
            <ModalButton
              colorVariant={ButtonColorVariant.Primary}
              disabled={isRequestInProgress}
              onClick={onApply}
              size={ButtonSize.Medium}
              width="100%"
            >
              {isApplyingDiscount ? (
                <Box
                  alignItems={AlignItems.Center}
                  display={Display.Flex}
                  justifyContent={JustifyContent.Center}
                  gap={2}
                >
                  <Spinner data-testid="apply-discount-modal-spinner" size={6} />
                  <span>Applying discount...</span>
                </Box>
              ) : (
                discount.applyCta
              )}
            </ModalButton>
            <Space size={[3, 4]} direction="vertical" />
            <ModalButton
              colorVariant={ButtonColorVariant.Secondary}
              disabled={isRequestInProgress}
              onClick={onSkip}
              size={ButtonSize.Medium}
              width="100%"
            >
              {isSkipping ? (
                <Box
                  alignItems={AlignItems.Center}
                  display={Display.Flex}
                  justifyContent={JustifyContent.Center}
                  gap={2}
                >
                  <Spinner data-testid="skip-order-modal-spinner" size={6} />
                  <span>Skipping box...</span>
                </Box>
              ) : (
                discount.skipCta
              )}
            </ModalButton>
            {hasSkipOrderError ? (
              <>
                <Space size={2} direction="vertical" />
                <SkipOrderErrorCard message={SKIP_ORDER_ERROR_MESSAGE} />
              </>
            ) : null}
            {hasApplyDiscountError ? (
              <>
                <Space size={2} direction="vertical" />
                <SkipOrderErrorCard message={APPLY_DISCOUNT_ERROR_MESSAGE} />
              </>
            ) : null}
          </Box>
        </ModalBody>
      </Modal>
    </>
  )
}
